/**
 * Resolve track/node metadata from study context and map to contributing roles.
 */

import { resolveContributingRoleIdsForTrack } from '../utils/roleTrackContribution';
import { loadPublishedRoles } from './rolesService';
import { isRoadmapUserCookbook } from './roadmapService';

/**
 * @param {Record<string, unknown>|null|undefined} deck
 * @param {Record<string, unknown>|null|undefined} card
 * @returns {string|null}
 */
export function resolveTrackIdFromDeckAndCard(deck, card) {
  const deckData = deck && typeof deck === 'object' ? deck : null;
  const cardData = card && typeof card === 'object' ? card : null;
  const candidates = [
    cardData?.trackId,
    cardData?.roadmapTrackId,
    deckData?.trackId,
    deckData?.roadmapTrackId
  ];
  for (const candidate of candidates) {
    const id = candidate != null ? String(candidate).trim() : '';
    if (id) return id;
  }
  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} deck
 * @param {Record<string, unknown>|null|undefined} card
 * @returns {string|null}
 */
export function resolveNodeIdFromDeckAndCard(deck, card) {
  const deckData = deck && typeof deck === 'object' ? deck : null;
  const cardData = card && typeof card === 'object' ? card : null;
  const candidates = [
    cardData?.nodeId,
    cardData?.roadmapNodeId,
    deckData?.nodeId,
    deckData?.roadmapNodeId
  ];
  for (const candidate of candidates) {
    const id = candidate != null ? String(candidate).trim() : '';
    if (id) return id;
  }
  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} deck
 * @returns {boolean}
 */
export function isRoleEligibleCookbookDeck(deck) {
  if (!deck || typeof deck !== 'object') return false;
  return deck.type === 'cookbook' || isRoadmapUserCookbook(deck);
}

/**
 * Resolve which roles should gain EXP for a cookbook study rep.
 *
 * @param {{
 *   deck?: Record<string, unknown>|null,
 *   card?: Record<string, unknown>|null,
 *   roles?: import('./rolesService').Role[]|null,
 * }} context
 * @returns {Promise<{ trackId: string|null, nodeId: string|null, roleIds: string[] }>}
 */
export async function getContributingRoleIdsForStudy(context = {}) {
  const { deck, card, roles } = context;

  if (!isRoleEligibleCookbookDeck(deck)) {
    return { trackId: null, nodeId: null, roleIds: [] };
  }

  const trackId = resolveTrackIdFromDeckAndCard(deck, card);
  if (!trackId) {
    return { trackId: null, nodeId: resolveNodeIdFromDeckAndCard(deck, card), roleIds: [] };
  }

  const nodeId = resolveNodeIdFromDeckAndCard(deck, card);
  const publishedRoles = roles ?? (await loadPublishedRoles());
  const roleIds = resolveContributingRoleIdsForTrack(trackId, publishedRoles, nodeId);

  return { trackId, nodeId, roleIds };
}

/**
 * @param {Record<string, unknown>|null|undefined} deck
 * @param {Record<string, unknown>|null|undefined} card
 * @param {import('./rolesService').Role[]|null|undefined} [roles]
 * @returns {string[]}
 */
export function getRolesForCard(deck, card, roles) {
  if (!isRoleEligibleCookbookDeck(deck)) return [];
  const trackId = resolveTrackIdFromDeckAndCard(deck, card);
  if (!trackId || !roles?.length) return [];
  const nodeId = resolveNodeIdFromDeckAndCard(deck, card);
  return resolveContributingRoleIdsForTrack(trackId, roles, nodeId);
}
